import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { companiesApi, vehiclesApi } from '../../api/services';
import { setCompanies, NO_COMPANY } from '../../store';
import { useAlerts } from '../../custom/Alerts';
import VehicleForm from './VehicleForm';
import PageHeader, { tableEmpty } from '../common/PageHeader';
import TableSearch, { filtriraj, useFitRows, usePage, PAGINATOR } from '../common/TableSearch';

export default function Vehicles({ active }) {
    const alerts = useAlerts();
    const dispatch = useDispatch();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editId, setEditId] = useState(undefined);
    const [search, setSearch] = useState('');
    const tableRef = useRef(null);
    const filtered = useMemo(() => filtriraj(rows, search, (v) => [v.identificationNumber, v.licenceNumber, v.make && v.make.name, v.model && v.model.name, v.year,
        v.company ? v.company.name : 'Bez kompanije']), [rows, search]);
    const perPage = useFitRows(tableRef, active, { dep: rows.length > 0 });
    const [first, setFirst] = usePage(filtered.length, perPage, search);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            /* kao SyncService: uz vozila se osvežava i keš kompanija (padajuće liste u formi) */
            const [vehicles, companies] = await Promise.all([vehiclesApi.all(), companiesApi.all()]);
            setRows(vehicles);
            dispatch(setCompanies([NO_COMPANY, ...companies]));
        } catch { /* toast */ }
        setLoading(false);
    }, [dispatch]);

    useEffect(() => { if (active) refresh(); }, [active]);

    const remove = async (v) => {
        if (!(await alerts.confirm('Potvrdi', `Da li ste sigurni da želite da obrišete vozilo '${v.identificationNumber} [${v.licenceNumber}]'?`, { severity: 'danger' }))) return;
        try { await vehiclesApi.remove(v.id); await refresh(); } catch { /* toast */ }
    };

    return (
        <>
            <PageHeader title="Pregled vozila" subtitle="Vozni park: broj šasije, registracija, marka i model.">
                <Button icon="pi pi-refresh" outlined severity="secondary" tooltip="Osveži" tooltipOptions={{ position: 'bottom' }} aria-label="Osveži" onClick={refresh} />
                <Button label="Dodaj vozilo" icon="pi pi-plus" onClick={() => setEditId(0)} />
            </PageHeader>
            <div className="panel">
                <TableSearch value={search} onChange={setSearch} placeholder="Pretraga (broj šasije, registracija, marka, kompanija...)" label="Pretraga vozila"
                    ukupno={rows.length} pronadjeno={filtered.length} jedinica="vozila" />
                <div ref={tableRef}>
                <DataTable value={filtered} loading={loading} dataKey="id" size="small" stripedRows removableSort
                    {...PAGINATOR} rows={perPage} first={first} onPage={(e) => setFirst(e.first)}
                    emptyMessage={tableEmpty(search.trim() ? 'Nijedno vozilo ne odgovara pretrazi' : 'Nijedno vozilo nije pronađeno', 'pi pi-truck')} onRowDoubleClick={(e) => setEditId(e.data.id)}
                    rowClassName={() => 'row-clickable'}>
                    <Column header="" headerClassName="actions" bodyClassName="actions" style={{ width: '5.5rem' }} body={(v) => (
                        <span>
                            <Button icon="pi pi-pencil" rounded text severity="secondary" onClick={() => setEditId(v.id)} aria-label="Izmeni vozilo" tooltip="Izmeni" />
                            <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => remove(v)} aria-label="Obriši vozilo" tooltip="Obriši" />
                        </span>
                    )} />
                    <Column field="identificationNumber" header="Broj šasije" sortable />
                    <Column field="licenceNumber" header="Reg. broj" sortable />
                    <Column header="Marka" sortField="make.name" sortable body={(v) => v.make && v.make.name} />
                    <Column header="Model" sortField="model.name" sortable body={(v) => v.model && v.model.name} />
                    <Column field="year" header="Godina proizvodnje" sortable />
                    <Column header="Kompanija" sortField="company.name" sortable body={(v) => (v.company ? v.company.name : <span className="muted">Bez kompanije</span>)} />
                </DataTable>
                </div>
                <p className="panel-note"><i className="pi pi-info-circle" aria-hidden="true" />Dvoklik na red otvara izmenu vozila.</p>
            </div>
            {editId !== undefined && <VehicleForm id={editId} onClose={() => setEditId(undefined)} onSaved={refresh} />}
        </>
    );
}
